import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../ui/accordion";
import { Download } from 'lucide-react';
import * as XLSX from 'xlsx';
// Using custom alert styling instead of missing Alert component
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../ui/select";
import { operationsBase, TABLES, escapeAirtableString, fetchAllClients } from '../../../lib/airtable';
import { fetchScheduledOffering, fetchSessionsForSO } from '../../../lib/airtable';
import { parseAttendanceFile, analyzeSessionColumns } from '../../../lib/attendanceImport';
import SOSearchDropdown from '../../SOSearchDropdown';

/**
 * Calculate Levenshtein distance between two strings
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Edit distance between strings
 */
function levenshteinDistance(str1, str2) {
  const matrix = [];
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  return matrix[str2.length][str1.length];
}

/**
 * Attendee Import Component - Admin interface for importing attendees from CSV/XLSX files
 *
 * Features:
 * - Auto-detects client/company columns and maps to Airtable clients
 * - 4-tier intelligent client matching (exact/partial on ClientID and Client Long Name)
 * - Supports Full Name column parsing with comma-separated format
 * - Title case formatting for names and companies
 * - Dry run mode for preview before import
 * - Real-time validation with warnings for mismatches
 * - XLSX template download
 *
 * @component
 * @returns {React.ReactElement} Attendee import interface
 */
export default function AttendeeImportNew() {
  // SO Configuration
  const [soNumber, setSoNumber] = useState('');
  const [soDetails, setSoDetails] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isLoadingSO, setIsLoadingSO] = useState(false);

  // File state
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Parsed data
  const [attendees, setAttendees] = useState([]);
  const [emailWarnings, setEmailWarnings] = useState([]);
  const [clientMismatchWarnings, setClientMismatchWarnings] = useState([]);
  const [editedAttendees, setEditedAttendees] = useState({}); // Stores edits for firstName, lastName, email
  const [existingAttendees, setExistingAttendees] = useState({}); // Track which emails already exist

  // Client data
  const [availableClients, setAvailableClients] = useState([]);
  const [clientMappings, setClientMappings] = useState({}); // Maps row index to selected client

  // Import state
  const [isDryRun, setIsDryRun] = useState(true); // Default to dry run for safety
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [error, setError] = useState(null);

  // Progress tracking
  const [importProgress, setImportProgress] = useState(0);
  const [currentAttendee, setCurrentAttendee] = useState('');
  const [currentSession, setCurrentSession] = useState('');

  /**
   * Download XLSX template with Name, Email, Company columns
   * Generates a properly formatted template file with example data
   */
  const downloadTemplate = useCallback(() => {
    // Create worksheet data with headers and one example row
    const templateData = [
      ['Name', 'Email', 'Company'],
      ['John Doe', 'john.doe@example.com', 'Acme Corporation'],
    ];

    // Create worksheet and workbook
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendees');

    // Set column widths for better readability
    ws['!cols'] = [
      { wch: 25 }, // Name
      { wch: 35 }, // Email
      { wch: 30 }, // Company
    ];

    // Generate and download file
    XLSX.writeFile(wb, 'attendee-import-template.xlsx');
  }, []);

  // Load available clients on mount
  useEffect(() => {
    async function loadClients() {
      try {
        const clients = await fetchAllClients();
        // fetchAllClients returns simplified objects with id, clientId, name, status
        const validClients = (clients || []).filter(c => c && c.id && c.name);
        setAvailableClients(validClients);
      } catch (err) {
        console.error('Failed to load clients:', err);
        setAvailableClients([]);
      }
    }
    loadClients();
  }, []);

  // Handle SO selection
  const handleSOSearch = useCallback(async (selectedSO) => {
    // Use the passed SO or fall back to the current state value
    const searchValue = selectedSO || soNumber;

    if (!searchValue) {
      setSoDetails(null);
      setSessions([]);
      return;
    }

    setIsLoadingSO(true);
    try {
      const offering = await fetchScheduledOffering(searchValue);
      const sessionData = await fetchSessionsForSO(searchValue);

      setSoDetails(offering);
      setSessions(sessionData);
      setError(null);
    } catch (err) {
      setError(`Failed to load SO: ${err.message}`);
      setSoDetails(null);
      setSessions([]);
    } finally {
      setIsLoadingSO(false);
    }
  }, [soNumber]);

  // File processing
  const processFile = useCallback(async (uploadedFile) => {
    if (!uploadedFile) return;

    if (!soDetails || sessions.length === 0) {
      setError('Please select a valid SO with sessions first');
      return;
    }

    try {
      setError(null);
      const { data } = await parseAttendanceFile(uploadedFile);

      if (!data || !data.headers || !data.rows) {
        setError('Invalid file format');
        return;
      }

      // Process headers to find columns
      const headers = data.headers;
      const nameColIndex = headers.findIndex(h =>
        h.toLowerCase() === 'name' || h.toLowerCase() === 'full name'
      );
      const firstNameIndex = headers.findIndex(h =>
        h.toLowerCase() === 'firstname' || h.toLowerCase() === 'first name'
      );
      const lastNameIndex = headers.findIndex(h =>
        h.toLowerCase() === 'lastname' || h.toLowerCase() === 'last name'
      );
      const emailIndex = headers.findIndex(h =>
        h.toLowerCase() === 'email'
      );
      const companyIndex = headers.findIndex(h =>
        ['company', 'client', 'organization', 'employer'].some(term =>
          h.toLowerCase().includes(term)
        )
      );

      // Analyze session columns
      const sessionMapping = analyzeSessionColumns({ headers, rows: data.rows }, sessions);

      // Process rows into attendees
      const processedAttendees = [];
      const warnings = [];
      const mappings = {};

      data.rows.forEach((row, idx) => {
        const rowData = row.data;

        // Extract name
        let firstName = '';
        let lastName = '';

        if (firstNameIndex >= 0 && lastNameIndex >= 0) {
          firstName = rowData[firstNameIndex] || '';
          lastName = rowData[lastNameIndex] || '';
        } else if (nameColIndex >= 0) {
          const fullName = rowData[nameColIndex] || '';
          const parts = fullName.trim().split(/\s+/);
          firstName = parts[0] || '';
          lastName = parts.slice(1).join(' ') || '';
        }

        const email = emailIndex >= 0 ? rowData[emailIndex] || '' : '';
        const company = companyIndex >= 0 ? rowData[companyIndex] || '' : '';

        // Check for email-name mismatches using pattern detection
        if (email && (firstName || lastName)) {
          const emailLocal = email.split('@')[0].toLowerCase();
          const firstNameLower = firstName.toLowerCase();
          const lastNameLower = lastName.toLowerCase();

          // Extract potential name parts from email
          // Common patterns: firstname.lastname, firstname_lastname, firstnamelastname, f.lastname, flastname
          let emailParts = [];

          // Split by common separators
          if (emailLocal.includes('.')) {
            emailParts = emailLocal.split('.');
          } else if (emailLocal.includes('_')) {
            emailParts = emailLocal.split('_');
          } else if (emailLocal.includes('-')) {
            emailParts = emailLocal.split('-');
          } else {
            // Try to detect camelCase or position where name might split
            // For single word emails, check if it could be firstlast or flast pattern
            emailParts = [emailLocal];

            // Check if email might be first initial + last name (e.g., "dchen" for "David Chen")
            if (emailLocal.length > 2 && firstNameLower.length > 0) {
              const firstInitial = firstNameLower[0];
              if (emailLocal[0] === firstInitial) {
                const possibleLastName = emailLocal.substring(1);
                emailParts = [firstInitial, possibleLastName];
              }
            }

            // Check if email contains full first name at the start
            if (firstNameLower && emailLocal.startsWith(firstNameLower)) {
              const possibleLastName = emailLocal.substring(firstNameLower.length);
              if (possibleLastName) {
                emailParts = [firstNameLower, possibleLastName];
              }
            }
          }

          // Check for mismatches
          let mismatchFound = false;
          let mismatchDetails = '';

          // Check each email part against first and last names
          emailParts.forEach(part => {
            if (part.length < 2) return; // Skip single letters (likely initials)

            // Check for similar but not exact matches (potential typos/variations)
            if (firstNameLower && part.length > 2) {
              // Check if part is similar to first name but not exact
              const firstNameMatch = firstNameLower.includes(part) || part.includes(firstNameLower);
              const isExactFirst = firstNameLower === part;

              if (!firstNameMatch && !isExactFirst) {
                // Check for common variations (chen/chan, etc)
                const distance = levenshteinDistance(part, firstNameLower);
                if (distance === 1 && part.length > 3) {
                  mismatchFound = true;
                  mismatchDetails = `Email contains "${part}" but first name is "${firstName}"`;
                }
              }
            }

            if (lastNameLower && part.length > 2) {
              // Check if part is similar to last name but not exact
              const lastNameMatch = lastNameLower.includes(part) || part.includes(lastNameLower);
              const isExactLast = lastNameLower === part;

              if (!lastNameMatch && !isExactLast) {
                // Check for common variations
                const distance = levenshteinDistance(part, lastNameLower);
                if (distance === 1 && part.length > 3) {
                  mismatchFound = true;
                  mismatchDetails = `Email contains "${part}" but last name is "${lastName}"`;
                }
              }
            }
          });

          // Also check if the email suggests a different name arrangement
          if (emailParts.length >= 2 && firstNameLower && lastNameLower) {
            const emailFirst = emailParts[0];
            const emailLast = emailParts[emailParts.length - 1];

            // Check if names might be swapped
            if (emailFirst === lastNameLower && emailLast === firstNameLower) {
              mismatchFound = true;
              mismatchDetails = `Email suggests "${lastName} ${firstName}" but name is "${firstName} ${lastName}" - names may be swapped`;
            }
          }

          if (mismatchFound) {
            warnings.push({
              row: idx + 2,
              type: 'name-mismatch',
              message: mismatchDetails
            });
          }
        }

        // Build attendance record for sessions
        const attendance = {};
        Object.entries(sessionMapping.sessionColumns || {}).forEach(([sessionNo, colData]) => {
          const colIndex = headers.indexOf(colData.csvColumn);
          if (colIndex >= 0) {
            const value = rowData[colIndex];
            attendance[sessionNo] = ['yes', 'true', '1', 'y', 'present', 'x'].includes(
              String(value).toLowerCase().trim()
            );
          }
        });

        // Auto-match client if possible with flexible matching
        if (company && availableClients.length > 0) {
          const companyLower = company.toLowerCase().trim();

          // Try exact match first
          let matchedClient = availableClients.find(c => {
            const clientName = c?.name;
            return clientName && clientName.toLowerCase().trim() === companyLower;
          });

          // If no exact match, try partial match (client contains company or vice versa)
          if (!matchedClient) {
            matchedClient = availableClients.find(c => {
              const clientName = c?.name;
              if (!clientName) return false;
              const clientLower = clientName.toLowerCase().trim();

              // Check if either contains the other, or if they share significant words
              return clientLower.includes(companyLower) ||
                     companyLower.includes(clientLower) ||
                     // Check for common words (e.g., "Avenue" matches "Avenue Code")
                     clientLower.split(/\s+/).some(word =>
                       word.length > 3 && companyLower.split(/\s+/).includes(word)
                     );
            });
          }

          if (matchedClient) {
            mappings[idx] = matchedClient.id;
          }
        }

        processedAttendees.push({
          rowNumber: idx + 2,
          firstName,
          lastName,
          email,
          company,
          attendance
        });
      });

      setAttendees(processedAttendees);
      setEmailWarnings(warnings);
      setClientMappings(mappings);
      setFile(uploadedFile);

      // Check for existing attendees
      if (processedAttendees.length > 0) {
        try {
          const emails = processedAttendees.map(a => a.email).filter(e => e);
          const uniqueEmails = [...new Set(emails)];

          // Build filter formula for batch checking
          const emailFilters = uniqueEmails.map(email =>
            `{Email} = "${escapeAirtableString(email)}"`
          ).join(', ');

          const filterFormula = uniqueEmails.length > 1
            ? `OR(${emailFilters})`
            : emailFilters;

          const existing = await operationsBase(TABLES.ATTENDEES).select({
            filterByFormula: filterFormula,
            fields: ['Email', 'First Name', 'Last Name', 'SO', 'Client']
          }).all();

          // Create a map of existing emails
          const existingMap = {};
          existing.forEach(record => {
            const email = record.get('Email');
            if (email) {
              existingMap[email.toLowerCase()] = {
                id: record.id,
                firstName: record.get('First Name'),
                lastName: record.get('Last Name'),
                sos: record.get('SO') || '',
                fields: {
                  Client: record.get('Client') || []
                }
              };
            }
          });

          setExistingAttendees(existingMap);

          // Build client mismatch warnings
          const clientMismatches = [];
          processedAttendees.forEach((attendee, idx) => {
            const email = attendee.email;
            if (!email) return;

            const existing = existingMap[email.toLowerCase()];
            if (!existing || !existing.fields?.Client || existing.fields.Client.length === 0) return;

            const existingClientId = existing.fields.Client[0];
            const csvClientId = mappings[idx];

            if (csvClientId && existingClientId !== csvClientId) {
              const existingClient = availableClients.find(c => c.id === existingClientId);
              const csvClient = availableClients.find(c => c.id === csvClientId);

              clientMismatches.push({
                row: attendee.rowNumber,
                type: 'client-mismatch',
                message: `${attendee.firstName} ${attendee.lastName} already exists with client "${existingClient?.name || 'Unknown'}" but CSV assigns "${csvClient?.name || 'Unknown'}". Using existing client.`
              });
            }
          });

          setClientMismatchWarnings(clientMismatches);
        } catch (error) {
          console.error('Error checking for existing attendees:', error);
          // Continue anyway - the import will still do individual checks
        }
      }

    } catch (err) {
      setError(`Failed to process file: ${err.message}`);
    }
  }, [soDetails, sessions, availableClients]);

  // Handle name/email edit and re-run mismatch detection
  const handleNameEdit = useCallback((idx, field, value) => {
    // Update edited attendees state
    setEditedAttendees(prev => ({
      ...prev,
      [idx]: {
        ...prev[idx],
        [field]: value
      }
    }));

    // Get the current attendee data
    const attendee = attendees[idx];
    const firstName = field === 'firstName' ? value : (editedAttendees[idx]?.firstName || attendee.firstName);
    const lastName = field === 'lastName' ? value : (editedAttendees[idx]?.lastName || attendee.lastName);
    const email = field === 'email' ? value : (editedAttendees[idx]?.email || attendee.email);

    // Re-run mismatch detection for this attendee
    if (email && (firstName || lastName)) {
      const emailLocal = email.split('@')[0].toLowerCase();
      const firstNameLower = firstName.toLowerCase();
      const lastNameLower = lastName.toLowerCase();

      // Extract potential name parts from email (same logic as in file processing)
      let emailParts = [];
      if (emailLocal.includes('.')) {
        emailParts = emailLocal.split('.');
      } else if (emailLocal.includes('_')) {
        emailParts = emailLocal.split('_');
      } else if (emailLocal.includes('-')) {
        emailParts = emailLocal.split('-');
      } else {
        emailParts = [emailLocal];
        if (emailLocal.length > 2 && firstNameLower.length > 0) {
          const firstInitial = firstNameLower[0];
          if (emailLocal[0] === firstInitial) {
            const possibleLastName = emailLocal.substring(1);
            emailParts = [firstInitial, possibleLastName];
          }
        }
        if (firstNameLower && emailLocal.startsWith(firstNameLower)) {
          const possibleLastName = emailLocal.substring(firstNameLower.length);
          if (possibleLastName) {
            emailParts = [firstNameLower, possibleLastName];
          }
        }
      }

      // Check for mismatches
      let mismatchFound = false;
      let mismatchDetails = '';

      emailParts.forEach(part => {
        if (part.length < 2) return;

        if (firstNameLower && part.length > 2) {
          const firstNameMatch = firstNameLower.includes(part) || part.includes(firstNameLower);
          const isExactFirst = firstNameLower === part;
          if (!firstNameMatch && !isExactFirst) {
            const distance = levenshteinDistance(part, firstNameLower);
            if (distance === 1 && part.length > 3) {
              mismatchFound = true;
              mismatchDetails = `Email contains "${part}" but first name is "${firstName}"`;
            }
          }
        }

        if (lastNameLower && part.length > 2) {
          const lastNameMatch = lastNameLower.includes(part) || part.includes(lastNameLower);
          const isExactLast = lastNameLower === part;
          if (!lastNameMatch && !isExactLast) {
            const distance = levenshteinDistance(part, lastNameLower);
            if (distance === 1 && part.length > 3) {
              mismatchFound = true;
              mismatchDetails = `Email contains "${part}" but last name is "${lastName}"`;
            }
          }
        }
      });

      if (emailParts.length >= 2 && firstNameLower && lastNameLower) {
        const emailFirst = emailParts[0];
        const emailLast = emailParts[emailParts.length - 1];
        if (emailFirst === lastNameLower && emailLast === firstNameLower) {
          mismatchFound = true;
          mismatchDetails = `Email suggests "${lastName} ${firstName}" but name is "${firstName} ${lastName}" - names may be swapped`;
        }
      }

      // Update warnings
      setEmailWarnings(prev => {
        const newWarnings = prev.filter(w => w.row !== idx + 2);
        if (mismatchFound) {
          newWarnings.push({
            row: idx + 2,
            type: 'name-mismatch',
            message: mismatchDetails
          });
        }
        return newWarnings.sort((a, b) => a.row - b.row);
      });
    } else {
      // Clear warnings for this row if no email/name to check
      setEmailWarnings(prev => prev.filter(w => w.row !== idx + 2));
    }
  }, [attendees, editedAttendees]);

  // Import execution
  /**
   * Execute the attendee import process
   *
   * Process:
   * 1. Validates required data (SO details, sessions, attendees)
   * 2. For each attendee:
   *    - Checks if attendee exists by email
   *    - Creates new attendee or updates existing one
   *    - Creates/updates session attendance records
   *    - Applies client assignments from CSV or manual selection
   * 3. Updates Participating Clients on SO
   *
   * @async
   * @returns {Promise<void>}
   */
  const executeImport = useCallback(async () => {
    if (!soDetails || !sessions.length || !attendees.length) {
      setError('Missing required data for import');
      return;
    }

    setIsImporting(true);
    setError(null);
    setImportResults(null);
    setImportProgress(0);
    setCurrentAttendee('');
    setCurrentSession('');

    const results = {
      attendeesCreated: 0,
      attendeesUpdated: 0,
      sessionRecordsCreated: 0,
      errors: [],
      warnings: []
    };

    const totalSteps = attendees.length;

    try {
      // Process each attendee
      for (let idx = 0; idx < attendees.length; idx++) {
        const attendee = attendees[idx];

        // Use edited names/email if available
        const firstName = editedAttendees[idx]?.firstName || attendee.firstName;
        const lastName = editedAttendees[idx]?.lastName || attendee.lastName;
        const email = editedAttendees[idx]?.email || attendee.email;

        // Update progress - show current attendee
        setCurrentAttendee(`${firstName} ${lastName}`);
        setCurrentSession('');

        // Skip if no email
        if (!email) continue;

        try {
          // In dry run mode, check for existing session attendance
          if (isDryRun) {
            // Check if this attendee exists in our pre-checked map
            const isExisting = existingAttendees[email.toLowerCase()];
            if (isExisting) {
              // Only count as "updated" if SO field actually needs updating
              const currentSO = isExisting.sos || '';
              const soNeedsUpdate = !currentSO.includes(soNumber);

              if (soNeedsUpdate) {
                results.attendeesUpdated++;
              }

              // Check for client mismatch
              const existingClientIds = isExisting.fields?.Client || [];
              const csvClientId = clientMappings[attendee.rowNumber - 2];

              if (csvClientId && existingClientIds.length > 0 && existingClientIds[0] !== csvClientId) {
                // Client mismatch detected - add warning
                const existingClient = availableClients.find(c => c.id === existingClientIds[0]);
                const csvClient = availableClients.find(c => c.id === csvClientId);
                results.warnings.push(
                  `Row ${attendee.rowNumber}: ${firstName} ${lastName} already exists with client "${existingClient?.name || 'Unknown'}" but CSV assigns "${csvClient?.name || 'Unknown'}". Using existing client.`
                );
              }

              // Query existing session attendance for THIS attendee directly
              const attendeeId = isExisting.id;
              const existingAttendance = await operationsBase(TABLES.SESSION_ATTENDANCE).select({
                filterByFormula: `FIND("${escapeAirtableString(attendeeId)}", ARRAYJOIN({Attendee}))`,
                fields: ['Session']
              }).all();

              // Build set of sessions that already have attendance records
              const existingSessionIds = new Set(
                existingAttendance
                  .map(record => record.fields.Session)
                  .filter(sessions => sessions && sessions.length > 0)
                  .map(sessions => sessions[0])
              );

              // Only count sessions that don't have attendance records yet
              const sessionsToCreate = sessions.filter(s => !existingSessionIds.has(s.id)).length;
              results.sessionRecordsCreated += sessionsToCreate;
            } else {
              results.attendeesCreated++;
              // New attendee - will need all session records
              results.sessionRecordsCreated += sessions.length;
            }
            continue;
          }

          // Check if attendee exists (using edited email)
          const existingAttendeesQuery = await operationsBase(TABLES.ATTENDEES).select({
            filterByFormula: `{Email} = "${escapeAirtableString(email)}"`,
            maxRecords: 1
          }).firstPage();

          let attendeeId;

          if (existingAttendeesQuery.length > 0) {
            // Update existing attendee
            attendeeId = existingAttendeesQuery[0].id;
            const currentSO = existingAttendeesQuery[0].fields.SO || '';

            // Check for client mismatch
            const existingClientIds = existingAttendeesQuery[0].fields.Client || [];
            const csvClientId = clientMappings[attendee.rowNumber - 2];

            if (csvClientId && existingClientIds.length > 0 && existingClientIds[0] !== csvClientId) {
              // Client mismatch detected - add warning
              const existingClient = availableClients.find(c => c.id === existingClientIds[0]);
              const csvClient = availableClients.find(c => c.id === csvClientId);
              results.warnings.push(
                `Row ${attendee.rowNumber}: ${firstName} ${lastName} already exists with client "${existingClient?.name || 'Unknown'}" but CSV assigns "${csvClient?.name || 'Unknown'}". Using existing client.`
              );
            }

            if (!currentSO.includes(soNumber)) {
              await operationsBase(TABLES.ATTENDEES).update(attendeeId, {
                'SO': currentSO ? `${currentSO}, ${soNumber}` : soNumber
              });
              results.attendeesUpdated++;
            }
          } else {
            // Create new attendee (use edited names/email if available)
            const attendeeData = {
              'First Name': firstName,
              'Last Name': lastName,
              'Email': email,
              'SO': soNumber,
              'Status': 'Registered'
            };

            // Add client if mapped
            const clientId = clientMappings[attendee.rowNumber - 2];
            if (clientId) {
              attendeeData.Client = [clientId];
            }

            const newRecord = await operationsBase(TABLES.ATTENDEES).create(attendeeData);
            attendeeId = newRecord.id;
            results.attendeesCreated++;
          }

          // Query existing session attendance for THIS attendee directly
          const existingAttendance = await operationsBase(TABLES.SESSION_ATTENDANCE).select({
            filterByFormula: `FIND("${escapeAirtableString(attendeeId)}", ARRAYJOIN({Attendee}))`,
            fields: ['Session', 'Present']
          }).all();

          // Build set of sessions that already have attendance records
          const existingSessionIds = new Set();
          const attendanceUpdateMap = new Map(); // sessionId -> {recordId, currentPresent}

          for (const record of existingAttendance) {
            const recordSessionIds = record.fields.Session || [];
            if (recordSessionIds.length > 0) {
              existingSessionIds.add(recordSessionIds[0]);
              attendanceUpdateMap.set(recordSessionIds[0], {
                id: record.id,
                present: record.fields.Present || false
              });
            }
          }

          // Create session attendance records only for sessions that don't exist yet
          for (const session of sessions) {
            // Update progress - show current session
            setCurrentSession(`Session ${session.sessionNo}`);

            const isPresent = attendee.attendance[session.sessionNo] || false;

            if (!existingSessionIds.has(session.id)) {
              // Create new attendance record
              await operationsBase(TABLES.SESSION_ATTENDANCE).create({
                'Attendee': [attendeeId],
                'Session': [session.id],
                'Present': isPresent
              });
              results.sessionRecordsCreated++;
              existingSessionIds.add(session.id); // Prevent duplicate within this run
            } else {
              // Check if we need to update the Present status
              const existing = attendanceUpdateMap.get(session.id);
              if (existing && existing.present !== isPresent) {
                await operationsBase(TABLES.SESSION_ATTENDANCE).update(existing.id, {
                  'Present': isPresent
                });
              }
            }
          }

          // Update progress after completing this attendee
          const progress = Math.round(((idx + 1) / totalSteps) * 100);
          setImportProgress(progress);

          // Small delay to respect rate limits
          await new Promise(resolve => setTimeout(resolve, 250));

        } catch (err) {
          results.errors.push(`Row ${attendee.rowNumber}: ${err.message}`);
          // Still update progress even on error
          const progress = Math.round(((idx + 1) / totalSteps) * 100);
          setImportProgress(progress);
        }
      }

      // Update participating clients in SO
      if (!isDryRun) {
        const uniqueClientIds = [...new Set(Object.values(clientMappings))];
        for (const clientId of uniqueClientIds) {
          try {
            const soRecord = await operationsBase(TABLES.SCHEDULED_OFFERINGS).find(soDetails.id);
            const currentClients = soRecord.fields['Participating Clients'] || [];

            if (!currentClients.includes(clientId)) {
              await operationsBase(TABLES.SCHEDULED_OFFERINGS).update(soDetails.id, {
                'Participating Clients': [...currentClients, clientId]
              });
            }
          } catch (err) {
            console.error('Failed to update participating clients:', err);
          }
        }
      }

      setImportResults(results);

    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setIsImporting(false);
    }
  }, [soDetails, sessions, attendees, clientMappings, isDryRun, soNumber, editedAttendees, existingAttendees, availableClients]);

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  }, [processFile]);

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle>Import Attendees</CardTitle>
          <CardDescription>
            Upload an Excel/CSV file to import attendee data and mark attendance
          </CardDescription>
        </CardHeader>
      </Card>

      {/* SO Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Select Scheduled Offering</CardTitle>
        </CardHeader>
        <CardContent>
          <SOSearchDropdown
            value={soNumber}
            onChange={setSoNumber}
            onSubmit={handleSOSearch}
            loading={isLoadingSO}
            placeholder="Enter SO number or search..."
          />

          {soDetails && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="font-medium">{soDetails.soid}</p>
              <p className="text-sm text-gray-600">
                {soDetails.clientName} - {soDetails.programName}
              </p>
              <p className="text-sm text-gray-500">
                {sessions.length} sessions found
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* File Upload */}
      {soDetails && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Upload File</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadTemplate}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download Template
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                dragActive ? 'border-blue-500 bg-blue-50' :
                file ? 'border-green-500 bg-green-50' : 'border-gray-300'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => processFile(e.target.files[0])}
              />

              {file ? (
                <div>
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-gray-600">
                    {attendees.length} attendees loaded
                  </p>
                </div>
              ) : (
                <p>Drop your Excel/CSV file here or click to browse</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Client Mismatch Warnings */}
      {clientMismatchWarnings.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
          <p className="font-medium mb-2">🔶 Client Mismatches Detected:</p>
          {clientMismatchWarnings.map((warning, idx) => (
            <p key={idx} className="text-sm">
              Row {warning.row}: {warning.message}
            </p>
          ))}
        </div>
      )}

      {/* Email Warnings */}
      {emailWarnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="font-medium mb-2">⚠️ Name/Email Mismatches Detected:</p>
          {emailWarnings.map((warning, idx) => (
            <p key={idx} className="text-sm">
              Row {warning.row}: {warning.message}
            </p>
          ))}
        </div>
      )}

      {/* Client Matching Info */}
      {attendees.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="matching-info">
                <AccordionTrigger className="text-sm font-medium text-blue-700">
                  ℹ️ How does client/company matching work?
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 text-sm text-gray-700">
                    <p>The system matches company names from your CSV to clients in Airtable using these fields:</p>

                    <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                      <p className="font-semibold text-blue-800 mb-2">Required Airtable Fields:</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-700">
                        <li><strong>Client Long Name</strong> - Full company name</li>
                        <li><strong>ClientID</strong> - Short company code</li>
                        <li><strong>Status</strong> - Must be set to "Active"</li>
                      </ul>
                    </div>

                    <p className="text-sm text-gray-600">
                      💡 If a client isn't being matched automatically, check that these fields are filled out in your Airtable Clients table.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Data Table */}
      {attendees.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Review Data</CardTitle>
            <div className="flex items-center space-x-2 mt-2">
              <Checkbox
                id="dryRun"
                checked={isDryRun}
                onCheckedChange={setIsDryRun}
              />
              <Label htmlFor="dryRun">
                Dry Run Mode (preview without importing)
              </Label>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-3 py-3 text-left text-sm font-semibold text-gray-900 w-20">Row</th>
                      <th className="border border-gray-200 px-3 py-3 text-left text-sm font-semibold text-gray-900 w-32">Status</th>
                      <th className="border border-gray-200 px-3 py-3 text-left text-sm font-semibold text-gray-900 min-w-[180px]">First Name</th>
                      <th className="border border-gray-200 px-3 py-3 text-left text-sm font-semibold text-gray-900 min-w-[180px]">Last Name</th>
                      <th className="border border-gray-200 px-3 py-3 text-left text-sm font-semibold text-gray-900 min-w-[280px]">Email</th>
                      <th className="border border-gray-200 px-3 py-3 text-left text-sm font-semibold text-gray-900 min-w-[160px]">
                        <div>
                          <div>Company</div>
                          <div className="text-xs font-normal text-gray-500">(from spreadsheet)</div>
                        </div>
                      </th>
                      <th className="border border-gray-200 px-3 py-3 text-left text-sm font-semibold text-gray-900 min-w-[240px]">Assign Client</th>
                    </tr>
                  </thead>
                <tbody>
                  {attendees.map((attendee, idx) => {
                    const hasWarning = emailWarnings.some(w => w.row === attendee.rowNumber);
                    const warning = emailWarnings.find(w => w.row === attendee.rowNumber);
                    const email = editedAttendees[idx]?.email || attendee.email;
                    const isExisting = email && existingAttendees[email.toLowerCase()];
                    const hasNoClient = !clientMappings[idx];

                    // Check for client mismatch
                    const hasClientMismatch = isExisting && isExisting.fields && clientMappings[idx] &&
                      isExisting.fields.Client &&
                      isExisting.fields.Client.length > 0 &&
                      isExisting.fields.Client[0] !== clientMappings[idx];

                    // Determine row background color based on issue type
                    // Priority: Name/email mismatch > Client mismatch > No Client > Duplicate (so most critical issues show)
                    let rowBgClass = '';
                    if (hasWarning) {
                      rowBgClass = 'bg-amber-50 hover:bg-amber-100'; // Name/email mismatch
                    } else if (hasClientMismatch) {
                      rowBgClass = 'bg-orange-50 hover:bg-orange-100'; // Client mismatch
                    } else if (hasNoClient) {
                      rowBgClass = 'bg-red-50 hover:bg-red-100'; // Missing client assignment
                    } else if (isExisting) {
                      rowBgClass = 'bg-blue-50 hover:bg-blue-100'; // Duplicate attendee
                    } else {
                      rowBgClass = 'hover:bg-gray-50'; // Normal row
                    }

                    return (
                      <tr key={idx} className={`${rowBgClass} transition-colors`}>
                        <td className="border border-gray-200 px-3 py-3 text-sm text-gray-700 align-top">
                          <div className="flex items-center gap-2">
                            {attendee.rowNumber}
                            {hasWarning && (
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold" title={warning?.message}>
                                !
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="border border-gray-200 px-3 py-3 align-top">
                          {isExisting ? (
                            <div className="space-y-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                Exists
                              </span>
                              <div className="text-[10px] text-gray-500">
                                {isExisting.sos.includes(soNumber) ? "in this SO" : "add to SO"}
                              </div>
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              New
                            </span>
                          )}
                        </td>
                        <td className="border border-gray-200 px-2 py-2 align-top">
                          <Input
                            className="w-full h-9 text-sm"
                            value={editedAttendees[idx]?.firstName ?? attendee.firstName}
                            onChange={(e) => handleNameEdit(idx, 'firstName', e.target.value)}
                            placeholder="First name"
                          />
                        </td>
                        <td className="border border-gray-200 px-2 py-2 align-top">
                          <Input
                            className="w-full h-9 text-sm"
                            value={editedAttendees[idx]?.lastName ?? attendee.lastName}
                            onChange={(e) => handleNameEdit(idx, 'lastName', e.target.value)}
                            placeholder="Last name"
                          />
                        </td>
                        <td className="border border-gray-200 px-2 py-2 align-top">
                          <Input
                            className={`w-full h-9 text-sm ${hasWarning ? 'border-amber-300 focus:ring-amber-500' : ''}`}
                            type="email"
                            value={editedAttendees[idx]?.email ?? attendee.email}
                            onChange={(e) => handleNameEdit(idx, 'email', e.target.value)}
                            placeholder="Email"
                          />
                          {hasWarning && warning && (
                            <div className="mt-1 text-xs text-amber-700 leading-tight">
                              {warning.message}
                            </div>
                          )}
                        </td>
                        <td className="border border-gray-200 px-3 py-3 text-sm text-gray-700 align-top">
                          <div className="whitespace-normal break-words">
                            {attendee.company || <span className="text-gray-400">—</span>}
                          </div>
                        </td>
                        <td className="border border-gray-200 px-2 py-2 align-top">
                          <Select
                            value={clientMappings[idx] || 'none'}
                            onValueChange={(value) => {
                              setClientMappings(prev => ({
                                ...prev,
                                [idx]: value === 'none' ? null : value
                              }));
                            }}
                          >
                            <SelectTrigger className="w-full h-9 text-sm">
                              <SelectValue placeholder="Select client..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No Client</SelectItem>
                              {availableClients.map(client => (
                                <SelectItem key={client.id} value={client.id}>
                                  {client?.name || 'Unnamed Client'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Row Highlighting Legend */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">Row Highlighting:</p>
              <div className="flex flex-wrap gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-4 bg-amber-50 border border-amber-200 rounded"></div>
                  <span className="text-gray-600">Name/Email Mismatch</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-4 bg-orange-50 border border-orange-200 rounded"></div>
                  <span className="text-gray-600">Client Mismatch</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-4 bg-red-50 border border-red-200 rounded"></div>
                  <span className="text-gray-600">No Client Assigned</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-4 bg-blue-50 border border-blue-200 rounded"></div>
                  <span className="text-gray-600">Attendee already exists</span>
                </div>
              </div>
            </div>

            {/* Import Actions */}
            <div className="mt-6 space-y-4">
              <Button
                onClick={executeImport}
                disabled={isImporting}
                className={isDryRun ? "bg-blue-600" : "bg-green-600"}
                size="lg"
              >
                {isImporting
                  ? "Processing..."
                  : isDryRun
                    ? "Run Dry Import (Test)"
                    : "Import Attendees"}
              </Button>

              {/* Progress Indicator */}
              {isImporting && !isDryRun && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="space-y-3">
                    {/* Progress Bar */}
                    <div>
                      <div className="flex justify-between text-sm font-medium text-blue-900 mb-2">
                        <span>Import Progress</span>
                        <span>{importProgress}%</span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${importProgress}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Current Status */}
                    <div className="text-sm space-y-1">
                      {currentAttendee && (
                        <p className="text-blue-800">
                          <span className="font-medium">Attendee:</span> {currentAttendee}
                        </p>
                      )}
                      {currentSession && (
                        <p className="text-blue-700">
                          <span className="font-medium">Adding to:</span> {currentSession}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {importResults && (
                <div className="flex-1 p-4 bg-gray-50 rounded">
                  <p className="font-medium">
                    {isDryRun ? "Dry Run Results:" : "Import Results:"}
                  </p>

                  {/* Show "no import needed" message if everything is zero */}
                  {importResults.attendeesCreated === 0 &&
                   importResults.attendeesUpdated === 0 &&
                   importResults.sessionRecordsCreated === 0 && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
                      <p className="text-sm text-blue-800 font-medium">
                        ✓ No import needed - all data already exists
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        All {attendees.length} attendee{attendees.length !== 1 ? 's' : ''} and their attendance records are already in the system
                      </p>
                    </div>
                  )}

                  <p className="text-sm mt-2">
                    {importResults.attendeesCreated} new attendees,
                    {importResults.attendeesUpdated} updated
                  </p>
                  {importResults.sessionRecordsCreated > 0 ? (
                    <p className="text-sm text-green-600">
                      ✓ {importResults.sessionRecordsCreated} session attendance record{importResults.sessionRecordsCreated !== 1 ? 's' : ''} {isDryRun ? 'to create' : 'created'}
                    </p>
                  ) : (
                    <p className="text-sm text-blue-600">
                      ✓ All attendees already have session attendance records
                    </p>
                  )}
                  {importResults.warnings && importResults.warnings.length > 0 && (
                    <div className="mt-2 text-orange-600 text-sm">
                      <p>Warnings:</p>
                      {importResults.warnings.map((warn, idx) => (
                        <p key={idx}>• {warn}</p>
                      ))}
                    </div>
                  )}
                  {importResults.errors.length > 0 && (
                    <div className="mt-2 text-red-600 text-sm">
                      <p>Errors:</p>
                      {importResults.errors.map((err, idx) => (
                        <p key={idx}>• {err}</p>
                      ))}
                    </div>
                  )}

                  {/* Always show review link */}
                  <a
                    href={`/${soNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                  >
                    → Review attendance for SO {soNumber}
                  </a>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}
    </div>
  );
}